import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authClient } from 'src/services/auth/client';
import { QUERIES } from 'src/utils/react-query';

export type OrgInvitationRow = {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt?: string | Date | null;
};

/** See the note in useOrgMembers: `@restavo/crud` needs plain functions, not hooks. */
export async function fetchInvitations(organizationId: string): Promise<OrgInvitationRow[]> {
  const { data, error } = await authClient.organization.listInvitations({
    query: { organizationId },
  });
  if (error) throw Object.assign(new Error(error.message), { code: (error as { code?: string }).code });
  return (data ?? []) as unknown as OrgInvitationRow[];
}

export async function inviteOrgMember(
  organizationId: string,
  input: { email: string; role: 'owner' | 'admin' | 'member' }
): Promise<void> {
  const { error } = await authClient.organization.inviteMember({
    email: input.email,
    role: input.role,
    organizationId,
  });
  if (error) throw Object.assign(new Error(error.message), { code: (error as { code?: string }).code });
}

export async function cancelOrgInvitation(invitationId: string): Promise<void> {
  const { error } = await authClient.organization.cancelInvitation({ invitationId });
  if (error) throw Object.assign(new Error(error.message), { code: (error as { code?: string }).code });
}

export function useInvitations(organizationId: string | null) {
  return useQuery({
    queryKey: [QUERIES.ORG_INVITATIONS, organizationId],
    enabled: Boolean(organizationId),
    queryFn: () => fetchInvitations(organizationId!),
  });
}

export function useInviteMember(organizationId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: 'owner' | 'admin' | 'member' }) =>
      inviteOrgMember(organizationId!, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERIES.ORG_INVITATIONS, organizationId] }),
  });
}

export function useCancelInvitation(organizationId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => cancelOrgInvitation(invitationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERIES.ORG_INVITATIONS, organizationId] }),
  });
}
